import Decimal from 'decimal.js';

import {Company} from '../models/company.model';
import {Invoice} from '../models/invoice.model';
import {InvoiceOption} from '../models/item.model';
import {SurchargeDiscountTypeEnum} from "../enums/surcharge-discount-type.enum";
import {PaymentRequestCalculationTypeEnum} from "../enums/payment-request-calculation-type.enum";
import {PaymentRequestAmountTypeEnum} from "../enums/payment-request-amount-type.enum";

export enum PricePrecision {
    Display = 2,
    SurchargeAndDiscount = 4,
    SubtotalAndTotalAndCardFeeRecovery = 6,
    SurchargePercentFactor = 8,
    TaxRateCoefficient = 8,
}

// Use this class for price calculations, to ensure that all price results align with our Main Backend
export class PriceCalculator {
    static round(value: number, precision: PricePrecision = PricePrecision.Display): number {
        return new Decimal(value)
            .toDecimalPlaces(precision, Decimal.ROUND_HALF_UP)
            .toNumber();
    }

    static multiply(a: string | number, b: string | number, scale = 2): number {
        return new Decimal(a)
            .times(new Decimal(b))
            .toDecimalPlaces(scale, Decimal.ROUND_DOWN)
            .toNumber();
    }

    static divide(a: string | number, b: string | number, scale = 2): number {
        return new Decimal(a)
            .div(new Decimal(b))
            .toDecimalPlaces(scale, Decimal.ROUND_DOWN)
            .toNumber();
    }

    static sum(a: string | number, b: string | number, scale = 2): number {
        return new Decimal(a)
            .plus(new Decimal(b))
            .toDecimalPlaces(scale, Decimal.ROUND_DOWN)
            .toNumber();
    }

    static subtract(a: string | number, b: string | number, scale = 2): number {
        return new Decimal(a)
            .minus(new Decimal(b))
            .toDecimalPlaces(scale, Decimal.ROUND_DOWN)
            .toNumber();
    }
}

const getDiscount = (subtotal: number, discountType: SurchargeDiscountTypeEnum, discountAmount: number, surcharge: number) => {
    // The calculations are based on Main Backend source code: file "BaseInvoice.php", function "calculateDiscount"
    if (discountType === SurchargeDiscountTypeEnum.Percent) {
        const discountPercentFactor = PriceCalculator.divide(discountAmount, 100, PricePrecision.SurchargeAndDiscount);
        let subtotalWithSurcharge = subtotal;
        if (surcharge > 0) {
            subtotalWithSurcharge = PriceCalculator.round(PriceCalculator.sum(subtotal, surcharge, PricePrecision.SurchargeAndDiscount));
        }
        return PriceCalculator.round(PriceCalculator.multiply(discountPercentFactor, subtotalWithSurcharge, PricePrecision.SurchargeAndDiscount));
    } else if (discountType === SurchargeDiscountTypeEnum.Fixed) {
        return PriceCalculator.round(PriceCalculator.multiply(discountAmount, 1, PricePrecision.SurchargeAndDiscount));
    }

    return 0;
}

const getSurcharge = (subtotal: number, surchargeType: SurchargeDiscountTypeEnum, surchargeAmount: number) => {
    // The calculations are based on Main Backend source code: file "BaseInvoice.php", function "calculateSurcharge"
    if (surchargeType === SurchargeDiscountTypeEnum.Percent) {
        const surchargePercentFactor = PriceCalculator.round(PriceCalculator.divide(surchargeAmount, 100, PricePrecision.SurchargePercentFactor), PricePrecision.SurchargePercentFactor);
        return PriceCalculator.round(PriceCalculator.multiply(subtotal, surchargePercentFactor, PricePrecision.SurchargeAndDiscount));
    } else if (surchargeType === SurchargeDiscountTypeEnum.Fixed) {
        return PriceCalculator.round(PriceCalculator.multiply(surchargeAmount, 1, PricePrecision.SurchargeAndDiscount));
    }

    return 0;
}

const getTax = (subtotal: number,
                taxableSubtotal: number, taxRate: number,
                surchargeType: SurchargeDiscountTypeEnum, surchargeAmount: number,
                discountType: SurchargeDiscountTypeEnum, discountAmount: number,
) => {
    // The calculations are based on Main Backend source code: file "BaseInvoice.php"
    //  function "CalculateSubtotalAndTax"
    //  function "retrieveTaxRate"
    //  function "calculateTax"
    const taxRateFraction: number = PriceCalculator.divide(taxRate, 100, PricePrecision.TaxRateCoefficient);
    let surchargeAmountFinal = 0;
    let surchargePercentFinal = 0;
    if (surchargeType === SurchargeDiscountTypeEnum.Fixed) {
        surchargeAmountFinal = surchargeAmount;
        surchargePercentFinal = 0;
    } else if (surchargeType === SurchargeDiscountTypeEnum.Percent) {
        surchargeAmountFinal = 0;
        surchargePercentFinal = surchargeAmount;
    }

    let discountAmountFinal = 0;
    let discountPercentFinal = 0;
    if (discountType === SurchargeDiscountTypeEnum.Fixed) {
        discountAmountFinal = discountAmount;
        discountPercentFinal = 0;
    } else if (discountType === SurchargeDiscountTypeEnum.Percent) {
        discountAmountFinal = 0;
        discountPercentFinal = discountAmount;
    }

    const subtotalWithSurcharge = (subtotal + surchargeAmountFinal) * (1 + surchargePercentFinal / 100);
    if (subtotalWithSurcharge < 0) {
        return 0
    }
    const taxRaw: number = (taxableSubtotal - discountAmountFinal * taxableSubtotal / (subtotalWithSurcharge || 1)) * taxRateFraction * (1 - discountPercentFinal / 100)
    return PriceCalculator.round(taxRaw);
}

const getTotal = (subtotal: number, surcharge: number, discount: number, tax: number): number => {
    // The calculations are based on Main Backend source code: file "BaseInvoice.php", function "CalculateSubtotalAndTax"
    let total: number = PriceCalculator.sum(subtotal, surcharge, PricePrecision.SubtotalAndTotalAndCardFeeRecovery)
    total = PriceCalculator.subtract(total, discount, PricePrecision.SubtotalAndTotalAndCardFeeRecovery)
    total = PriceCalculator.sum(total, tax, PricePrecision.SubtotalAndTotalAndCardFeeRecovery)
    return total;
}

const getAmountRemaining = (total: number, amountPaidAsNumber: number): number => {
    return PriceCalculator.subtract(total, amountPaidAsNumber, PricePrecision.SubtotalAndTotalAndCardFeeRecovery)
}

export interface InvoiceSummary {
    surcharge: string;
    discount: string;
    subtotal: string;
    tax: string;
    total: string;
    amountRemaining: string;
}


// 3.000 -> '$3,000.00' or '$3,000'
export const convertFloatToFormattedPrice = (price: number, currencySign: string = '$', shouldBeInteger: boolean = false): string => {
    const fraction: number = shouldBeInteger ? 0 : 2;
    const priceRounded: number = PriceCalculator.round(price, fraction);
    const options: Intl.NumberFormatOptions | null = {
        minimumFractionDigits: fraction,
        maximumFractionDigits: fraction,
    }

    return `${currencySign}${new Intl.NumberFormat('en-US', options).format(priceRounded)}`;
};

// '$3,000.00' -> 3.000
export const convertFormattedPriceToFloat = (formattedPrice: string | undefined): number => {
    if (!formattedPrice) {
        return 0.00;
    }

    return parseFloat(formattedPrice.replace(/[^\d.]/g, ''));
};

export const calculateInvoiceSummary = (
    company: Company,
    invoice: Invoice,
    isSingleOption: boolean,
    selectedOption: InvoiceOption | null,
    selectedAddOnOptions: InvoiceOption[]
): InvoiceSummary | null => {
    const companyCurrencySign: string = company.currency_sign;
    const discountType: SurchargeDiscountTypeEnum = invoice.discount_type;
    const surchargeType: SurchargeDiscountTypeEnum = invoice.surcharge_type;
    const discountAmountAsNumber = invoice.discount_amount_as_number;
    const surchargeAmountAsNumber = invoice.surcharge_amount_as_number;
    const taxRateAsNumber: number = invoice.tax_rate_as_number;
    const amountPaidAsNumber: number = invoice.amount_paid_as_number;

    // #1 Calculate the Base Prices (those, that have no Card Fee Recovery applied)
    //  To avoid calculation results discrepancies, we should calculate Base Prices first,
    //  and only then apply Card Fee Recovery percentage to them (if applicable)
    let subtotal = 0;
    let taxableSubtotal = 0;
    let lineItemTax = 0;

    if (selectedOption && !isSingleOption) {
        subtotal += selectedOption.subtotal_without_card_fee_recovery_as_number;
        taxableSubtotal += selectedOption.taxable_subtotal_without_card_fee_recovery;
        lineItemTax += selectedOption.tax_without_card_fee_recovery;
    } else {
        return null;
    }

    for (const option of selectedAddOnOptions) {
        subtotal += option.subtotal_without_card_fee_recovery_as_number;
        taxableSubtotal += option.taxable_subtotal_without_card_fee_recovery;
        lineItemTax += option.tax_without_card_fee_recovery;
    }

    const surcharge = getSurcharge(subtotal, surchargeType, surchargeAmountAsNumber)
    const discount = getDiscount(subtotal, discountType, discountAmountAsNumber, surcharge)
    let tax = getTax(subtotal, taxableSubtotal, taxRateAsNumber, surchargeType, surchargeAmountAsNumber, discountType, discountAmountAsNumber)
    if (invoice.is_invoice_line_tax) {
        tax = lineItemTax;
    }
    const totalPrecise = getTotal(subtotal, surcharge, discount, tax);
    const amountRemainingPrecise: number = getAmountRemaining(totalPrecise, amountPaidAsNumber);

    return {
        surcharge: convertFloatToFormattedPrice(surcharge, companyCurrencySign),
        discount: convertFloatToFormattedPrice(-discount, companyCurrencySign),
        subtotal: convertFloatToFormattedPrice(subtotal, companyCurrencySign),
        tax: convertFloatToFormattedPrice(tax, companyCurrencySign),
        total: convertFloatToFormattedPrice(totalPrecise, companyCurrencySign),
        amountRemaining: convertFloatToFormattedPrice(amountRemainingPrecise, companyCurrencySign),
    }
}

export const getInvoicePaymentAmount = (
    invoice: Invoice,
    invoiceTotal: string,
    invoiceTotalWithoutCardFeeRecovery: string,
    invoiceAmountDue: string,
    invoiceAmountDueWithoutCardFeeRecovery: string,
    withCardFeeRecovery: boolean,
): string => {
    // The calculations are based on Reports Backend source code: file "meta_invoice.py"
    // function "get_payment_request_amount_value"
    const paymentRequestAmountType: PaymentRequestAmountTypeEnum = invoice.payment_request_amount_type
    const paymentRequestAmount: string = invoice.payment_request_amount;

    if (paymentRequestAmountType && paymentRequestAmount && paymentRequestAmountType === PaymentRequestAmountTypeEnum.Percent) {
        try {
            const percentCoefficient = PriceCalculator.divide(parseFloat(paymentRequestAmount), 100, PricePrecision.SubtotalAndTotalAndCardFeeRecovery);

            // 1. Define amount to take the percent from
            let amountToTakePercentOf: number;
            if (invoice.is_invoice && invoice.payment_request_calculation_type === PaymentRequestCalculationTypeEnum.RemainingBalance) {
                amountToTakePercentOf = convertFormattedPriceToFloat(withCardFeeRecovery ? invoiceAmountDue : invoiceAmountDueWithoutCardFeeRecovery);
            } else {
                amountToTakePercentOf = convertFormattedPriceToFloat(withCardFeeRecovery ? invoiceTotal : invoiceTotalWithoutCardFeeRecovery);
            }

            // 2. Calculate amount based on the percent
            let amount = PriceCalculator.multiply(amountToTakePercentOf, percentCoefficient, PricePrecision.SubtotalAndTotalAndCardFeeRecovery);

            // 3. Ensure amount does not exceed Remaining Balance
            const total = convertFormattedPriceToFloat(withCardFeeRecovery ? invoiceTotal : invoiceTotalWithoutCardFeeRecovery);
            const remainingBalance = getAmountRemaining(total, invoice.amount_paid_as_number);
            if (amount > remainingBalance) {
                amount = remainingBalance;
            }

            // 4. Round and format amount
            return PriceCalculator.round(amount).toFixed(2);
        } catch (e) {
            // no-op
        }
    }

    // Fallback to what server has provided
    return convertFormattedPriceToFloat(withCardFeeRecovery ? invoice.payment_request_amount_value : invoice.payment_request_amount_value_without_card_fee_recovery).toFixed(2);
}
